import { useCallback, useState } from "react";

import type { OrderSort } from "gql/graphql";

interface IServerSideStore {
  pagination: { pageIndex: number; pageSize: number; cursor: string };
  setPageIndex: (index: number) => void;
  setPageSize: (size: number) => void;
  setPageCursor: (cursor: string) => void;
  sortBy: { field: string; order: `${OrderSort}` }[];
  setSortBy: (field: string, order: `${OrderSort}`) => void;
  resetSortBy: () => void;
}

const initialPagination = { cursor: "", pageIndex: 0, pageSize: 10 };
const initialSortBy: { field: string; order: `${OrderSort}` }[] = [];

type TPagination = typeof initialPagination;

const useTableServerSideStore = (): IServerSideStore => {
  const [pagination, setPagination] = useState(initialPagination);
  const [sortBy, setSortBy] = useState(initialSortBy);

  const resetSortBy = useCallback((): void => {
    setSortBy(initialSortBy);
  }, [setSortBy]);

  const updateSortBy = useCallback(
    (field: string, order: `${OrderSort}`): void => {
      setSortBy([{ field, order }]);
    },
    [setSortBy],
  );

  const setPageIndex = useCallback(
    (index: number): void => {
      setPagination((prev): TPagination => ({ ...prev, pageIndex: index }));
    },
    [setPagination],
  );

  const setPageSize = useCallback(
    (size: number): void => {
      setPagination((prev): TPagination => ({ ...prev, pageSize: size }));
    },
    [setPagination],
  );

  const setPageCursor = useCallback(
    (cursor: string): void => {
      setPagination((prev): TPagination => ({ ...prev, cursor }));
    },
    [setPagination],
  );

  return {
    pagination,
    resetSortBy,
    setPageCursor,
    setPageIndex,
    setPageSize,
    setSortBy: updateSortBy,
    sortBy,
  };
};

export { useTableServerSideStore };
